//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { assertNever } from '@/utils/AssertNever';
import { type PanelAnchor } from '@/services/apiTypes';

// Every measurement a panel needs, kept in one pure module: jsdom has no
// layout, so positioning asserted through a rendered component can pass while
// asserting nothing. Here it is arithmetic, and arithmetic is testable.

export interface PanelSize {
  w: number;
  h: number;
}

export interface PanelRect extends PanelSize {
  x: number;
  y: number;
}

// The region a panel may occupy, in viewport coordinates. A non-positive
// width or height means the region has not been measured yet rather than
// that it is empty, so it imposes no ceiling on a panel's size.
export interface PanelBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

// Below this a panel has no room for its own title bar and controls.
export const MIN_PANEL_SIZE: PanelSize = { w: 280, h: 200 };

// How far an anchored panel sits from the edge it is anchored to.
export const EDGE_MARGIN = 8;

// How far each panel steps off an already-occupied corner.
export const CASCADE_STEP = 28;

// The corner the header's skip links reveal themselves into. Panels stack
// above the links, and a panel over that corner leaves them reachable by
// keyboard -- which is who they are for -- but unclickable, so the corner is
// reserved here rather than fought over with z-index.
//
// Deliberately generous: the links size themselves from their text and the
// theme's type scale, neither of which a pure module can measure.
// Over-estimating costs a strip of one corner that panels start below;
// under-estimating costs the affordance.
export const SKIP_LINK_RESERVE: PanelSize = { w: 240, h: 48 };

/**
 * The highest a panel of this origin and width may sit.
 *
 * Below the reserved corner while the panel overlaps it horizontally, and the
 * region's own top edge everywhere else -- so the reserve costs the corner
 * rather than the whole top edge. A panel dragged clear of the strip's
 * columns can still reach `bounds.top`.
 */
const highestTop = (x: number, w: number, bounds: PanelBounds): number =>
  x < bounds.left + SKIP_LINK_RESERVE.w && x + w > bounds.left ?
    bounds.top + SKIP_LINK_RESERVE.h : bounds.top;

// A spec minimum can raise a panel's floor above the global one, never
// lower it: MIN_PANEL_SIZE is the least any panel may shrink to.
const sizeFloor = (min: number | undefined, globalMin: number): number =>
  Math.max(min ?? globalMin, globalMin);

/**
 * Reconcile a requested size with the floor and the available room.
 *
 * Order matters: the floor is applied first and the bounds last, so bounds
 * smaller than the floor win. A panel that does not fit is cramped; a panel
 * larger than the region it lives in has controls the user cannot reach.
 *
 * A non-positive bounds axis is an unmeasured region, not a zero-sized one,
 * and is skipped: the host's size is unknown before its first measurement,
 * and a zero ceiling there would hand back a 0x0 panel.
 */
export const clampSize = (
  size: PanelSize, min: PanelSize | undefined, bounds: PanelBounds
): PanelSize => {
  const flooredW = Math.max(size.w, sizeFloor(min?.w, MIN_PANEL_SIZE.w));
  const flooredH = Math.max(size.h, sizeFloor(min?.h, MIN_PANEL_SIZE.h));
  return {
    w: bounds.width > 0 ? Math.min(flooredW, bounds.width) : flooredW,
    h: bounds.height > 0 ? Math.min(flooredH, bounds.height) : flooredH
  };
};

// Which edges a resize drag moves. The eight of a desktop window: a pointer
// aiming at any border gets the resize it expects, rather than having to find
// one privileged corner.
export const PANEL_RESIZE_HANDLES = [
  'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'
] as const;

export type PanelResizeHandle = typeof PANEL_RESIZE_HANDLES[number];

/**
 * The largest a panel may be resized to from this handle, on each axis.
 *
 * Measured from the edge the drag pins rather than from the region: dragging
 * east the room is whatever lies right of the panel's own left edge, and
 * dragging west it is whatever lies left of its right edge. A ceiling of the
 * region's full extent would let a panel near a far edge be dragged past it
 * and pulled back only on release.
 *
 * A non-positive bounds axis is an unmeasured region, not a zero-sized one,
 * and so imposes no ceiling: passed through, the absence of a constraint
 * would become the tightest one there is and clamp a resize to nothing.
 * `Infinity` is what react-resizable defaults `maxConstraints` to, so it
 * reads there as that library's own "no constraint" rather than a sentinel of
 * ours.
 *
 * A north drag stops at the reserved corner for the same reason a placement
 * does, and reads it off the panel's current left edge. A north-west drag
 * widening into the strip's columns is the one case that can outrun that;
 * the commit clamp is what catches it.
 */
export const resizeCeiling = (
  handle: PanelResizeHandle, rect: PanelRect, bounds: PanelBounds
): [number, number] => [
  bounds.width <= 0 ? Infinity : Math.max(handle.includes('w') ?
    rect.x + rect.w - bounds.left : bounds.left + bounds.width - rect.x, 0),
  bounds.height <= 0 ? Infinity : Math.max(handle.includes('n') ?
    rect.y + rect.h - highestTop(rect.x, rect.w, bounds) :
    bounds.top + bounds.height - rect.y, 0)
];


/**
 * The rect a resize drag lands on: the dragged edge follows the pointer and
 * the opposite edge stays where it was.
 *
 * The library reports a size and the handle it came from, never an origin, so
 * a north or west drag has to be turned into an origin here -- left alone,
 * the panel would grow away from the edge being dragged.
 *
 * Floor and ceiling are applied to the size before the origin is derived from
 * it, so a drag that pushes past either stops with the pinned edge still
 * pinned. Deriving first and clamping after would walk the panel across the
 * screen for as long as the pointer kept going.
 *
 * Precedence matches `clampSize`: the floor first and the region last, so a
 * region smaller than the floor cramps the panel rather than letting it
 * overflow. An unmeasured axis imposes no ceiling, as it does everywhere else
 * in this module.
 */
export const resizeFrom = (
  handle: PanelResizeHandle, rect: PanelRect, size: PanelSize,
  min: PanelSize | undefined, bounds: PanelBounds
): PanelRect => {
  const floorW = sizeFloor(min?.w, MIN_PANEL_SIZE.w);
  const floorH = sizeFloor(min?.h, MIN_PANEL_SIZE.h);
  const movesWest = handle.includes('w');
  const movesEast = handle.includes('e');
  const movesNorth = handle.includes('n');
  const movesSouth = handle.includes('s');

  const [roomW, roomH] = resizeCeiling(handle, rect, bounds);

  const w = movesWest || movesEast ?
    Math.min(Math.max(size.w, floorW), roomW) : rect.w;
  const h = movesNorth || movesSouth ?
    Math.min(Math.max(size.h, floorH), roomH) : rect.h;

  return {
    x: movesWest ? rect.x + rect.w - w : rect.x,
    y: movesNorth ? rect.y + rect.h - h : rect.y,
    w,
    h
  };
};

/**
 * The top-left corner a freshly opened panel takes, from its anchor.
 *
 * An unmeasured axis has no far edge to anchor against, so every anchor on
 * that axis resolves to its near edge; the axes are judged separately, as in
 * `clampSize` and `clampToBounds`. Without this a panel opened before its
 * host was measured would be placed at a negative coordinate, which nothing
 * downstream would pull back -- clamping an unmeasured axis is a no-op.
 */
export const resolveAnchor = (
  anchor: PanelAnchor, size: PanelSize, bounds: PanelBounds
): { x: number; y: number } => {
  const left = bounds.left + EDGE_MARGIN;
  const top = bounds.top + EDGE_MARGIN;
  const right = bounds.width > 0 ?
    bounds.left + bounds.width - size.w - EDGE_MARGIN : left;
  const bottom = bounds.height > 0 ?
    bounds.top + bounds.height - size.h - EDGE_MARGIN : top;
  const middle = bounds.height > 0 ?
    bounds.top + Math.round((bounds.height - size.h) / 2) : top;

  switch (anchor) {
    case 'top-left': return { x: left, y: top };
    case 'top-right': return { x: right, y: top };
    case 'center-left': return { x: left, y: middle };
    case 'center-right': return { x: right, y: middle };
    case 'bottom-left': return { x: left, y: bottom };
    case 'bottom-right': return { x: right, y: bottom };
    default: return assertNever(anchor);
  }
};

/**
 * Keep a panel's origin within `keep` of its bounds on each axis, resizing
 * before repositioning.
 *
 * `keep` is the fraction of the panel's own extent that must stay on the
 * near side of the far edges: 1 pins it wholly inside, as `clampToBounds`
 * does for placement; `KEEP_ON_SCREEN` leaves most of a parked panel off
 * screen while `clampToReach` keeps a piece of it reachable.
 *
 * The top edge does not take `keep` -- both wrappers use the hard
 * `highestTop` floor there, so a panel can never hide its own title bar
 * above the reserved corner, which is the only handle a pointer has to drag
 * it back with.
 *
 * The reserved corner is a floor on the origin rather than an absolute bar:
 * a region too short to hold the panel below the strip puts it back on
 * screen and lets it overlap, since a panel nobody can reach is worse than a
 * skip link nobody can click.
 *
 * An unmeasured axis constrains neither the size nor the origin: with no
 * extent, the far edge sits behind the near one and clamping to it would put
 * the panel off-screen. The axes are judged separately, since a region can
 * be measured on one axis and not the other partway through a layout.
 *
 * `min` is the spec's own floor and must ride along on every call that has
 * one: this clamp runs on each bounds change, and clamping with only the
 * shared floor discards the spec minimum the first time a region shrinks
 * below it -- permanently, because the resize handle enforces the spec
 * minimum and so refuses the very drag that would recover it. `clampToBounds`
 * passes `undefined` deliberately: placement uses the global `MIN_PANEL_SIZE`
 * floor, not a spec minimum threaded through the cascade.
 */
const clampInto = (
  rect: PanelRect, bounds: PanelBounds, keep: number,
  min: PanelSize | undefined
): PanelRect => {
  const size = clampSize({ w: rect.w, h: rect.h }, min, bounds);
  const keptW = size.w * keep;
  const keptH = size.h * keep;
  const minX = bounds.left + keptW - size.w;
  const maxX = bounds.left + bounds.width - keptW;
  const maxY = bounds.top + bounds.height - keptH;
  const x = bounds.width > 0 ? Math.min(Math.max(rect.x, minX), maxX) : rect.x;
  return {
    x,
    y: bounds.height > 0 ?
      Math.min(Math.max(rect.y, highestTop(x, size.w, bounds)), maxY) : rect.y,
    w: size.w,
    h: size.h
  };
};

/**
 * Keep a panel wholly inside its bounds, resizing before repositioning.
 *
 * This is placement: where a panel goes when nobody has put it anywhere --
 * a fresh open, a reset, each candidate corner the cascade tries. A panel the
 * user has since moved is held by `clampToReach` instead, which lets them
 * park it against an edge. Somewhere the user has not chosen is somewhere
 * wholly on screen.
 */
export const clampToBounds = (
  rect: PanelRect, bounds: PanelBounds
): PanelRect => clampInto(rect, bounds, 1, undefined);

// How much of a panel has to stay inside its bounds when the user parks it
// against an edge. A fraction rather than a count of pixels, so a large panel
// leaves a large target: what has to remain is a piece worth aiming at, and
// what that is scales with the thing being aimed at.
export const KEEP_ON_SCREEN = 0.25;

/**
 * Keep a panel reachable rather than wholly inside its bounds.
 *
 * A user parking a panel mostly off-screen is arranging their workspace, and
 * hauling the whole thing back refuses the arrangement. What has to survive
 * is the means of undoing it: a quarter of the panel at whichever edge it
 * hangs off, which always carries a piece of the title bar, since the bar
 * spans the full width along the top; above the top edge the bar is the
 * first thing to leave, and it is the only handle a pointer has, which is
 * why that edge is the one exception `clampInto` makes to `keep`.
 */
export const clampToReach = (
  rect: PanelRect, bounds: PanelBounds, min?: PanelSize
): PanelRect => clampInto(rect, bounds, KEEP_ON_SCREEN, min);

/**
 * Step a panel off any corner already taken.
 *
 * Two panels sharing a corner are indistinguishable, and the second looks
 * like the first failed to open. Each candidate is clamped before it is
 * compared, since against an edge several unclamped candidates collapse onto
 * the same clamped point -- comparing before clamping would accept a corner
 * that is in fact taken.
 *
 * A step that cannot move the clamped point means the cascade path has
 * saturated against the far edges. Corners remain free up and left of it, but
 * the down-right path this cascade walks has no room, so the panel is
 * returned on a taken corner: overlapping is honest, and better than a search
 * that cannot advance.
 */
export const cascadeOffset = (
  rect: PanelRect, taken: PanelRect[], bounds: PanelBounds
): PanelRect => {
  const isTaken = (at: PanelRect): boolean =>
    taken.some((t) => t.x === at.x && t.y === at.y);
  let next = clampToBounds(rect, bounds);
  while (isTaken(next)) {
    const stepped = clampToBounds(
      { ...next, x: next.x + CASCADE_STEP, y: next.y + CASCADE_STEP }, bounds
    );
    // Each step moves down and right or not at all, so this terminates.
    if (stepped.x === next.x && stepped.y === next.y) break;
    next = stepped;
  }
  return next;
};
